import { onlineStatusService } from '../services/online-status.service';
import type { CleanupTask } from './odin-db-types';

let worker: Worker | null = null;
let unsubscribeOnline: (() => void) | null = null;

interface StartConfig {
  dbName: string;
  dbVersion: number;
  tasks: CleanupTask[]; // Tasks to be handled by the worker
  onCleanupComplete?: () => void;
}

/**
 * Starts or restarts the database cleanup Web Worker with the given configuration.
 *
 * Terminates any existing worker, creates a new module Worker for `db-cleanup-worker.ts`,
 * attaches message/error handlers, and posts a start command containing database details and tasks.
 * Executes `onCleanupComplete` only when the worker reports a "complete" status with a positive `deletedCount`.
 *
 * @param config Configuration for the cleanup process.
 * @param config.dbName Name of the IndexedDB database to clean.
 * @param config.dbVersion Version of the database.
 * @param config.tasks Array of task descriptors sent to the worker.
 * @param config.onCleanupComplete Optional callback invoked after successful cleanup with deletions.
 *
 * @remarks Worker errors are logged to the console; no exceptions are thrown to the caller.
 *
 * @returns void
 */
export function startCleanupWorker(config: StartConfig) {
  const { dbName, dbVersion, tasks, onCleanupComplete } = config;

  if (worker) {
    worker.terminate();
  }

  worker = new Worker(new URL('./db-cleanup-worker.ts', import.meta.url), {
    type: 'module',
  });

  // The worker's message handler now calls the generic callback
  worker.onmessage = (event) => {
    if (event.data?.status === 'complete' && event.data.deletedCount > 0) {
      console.log('[Main Thread] Worker finished cleanup. Executing callback.');
      onCleanupComplete?.();
    } else if (event.data?.status === 'error') {
      console.error('[Main Thread] Received error from DB cleanup worker:', event.data.error);
    }
  };

  worker.onerror = (error) => {
    console.error('[Main Thread] Error in DB cleanup worker:', error);
  };

  // Send the start command with configuration to the worker
  worker.postMessage({ command: 'start', dbName, dbVersion, tasks, onlineStatus: onlineStatusService.getStatus() });

  // Subscribe to online status changes and notify the worker
  unsubscribeOnline = onlineStatusService.subscribe((isOnline) => {
    worker?.postMessage({ command: 'update-online-status', onlineStatus: isOnline });
  });
}

/**
 * Stops the currently running database cleanup worker, if one exists.
 *
 * Sends a final 'stop' command to allow the worker to perform any graceful shutdown
 * logic, then immediately terminates the worker thread and clears the local reference.
 *
 * If no worker is active, the function exits without side effects.
 */
export function stopCleanupWorker() {
  if (unsubscribeOnline) {
    unsubscribeOnline();
    unsubscribeOnline = null;
  }

  if (worker) {
    console.log('[Main Thread] Stopping DB cleanup worker.');
    worker.postMessage({ command: 'stop' });
    worker.terminate();
    worker = null;
  }
}
